import React from "react";
import { Form, Button } from 'react-bootstrap'
import BaseComponent from './BaseComponent'
import { IIncidentInfo, IViolationList, IViolationData } from "../classes/Domain";
import Utilities from "../classes/Utilities";
import { validationUtils } from "../validation/Validation";
import { Calendar } from "./Calendar";
import moment from "moment";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { ValidatableForm } from "./ValidatableForm";

interface IState {
    category: string,
    violation: string,
    violationCode: string,
    violationDate: Date,
    convictionDate: Date | null,
    categoryViolations: Array<IViolationData>
}

interface IProps {
    index: number,
    incident: IIncidentInfo,
    onChange(incident: IIncidentInfo, index: number): void,
    onDelete(index: number): void,
    violations: IViolationList,
    errors: any
}

class IncidentInfo extends BaseComponent<IProps, IState> {
	constructor(props: IProps) {
		super(props);

        this.state = {
            category: this.props.incident.category,
            violation: this.props.incident.violation,
            violationCode: this.props.incident.violationCode,
            violationDate: this.props.incident.violationDate,
            convictionDate: this.props.incident.convictionDate,
            categoryViolations: this.props.incident.category
                ? this.props.violations.violationGroups.filter(v => v.groupName === this.props.incident.category).flatMap(x => x.violations)
                : this.props.violations.violationGroups.flatMap(x => x.violations)
		};

        this.onIncidentCategoryChange = this.onIncidentCategoryChange.bind(this);
        this.onIncidentViolationDateChange = this.onIncidentViolationDateChange.bind(this);
        this.onIncidentConvictionDateChange = this.onIncidentConvictionDateChange.bind(this);
        this.onIncidentDataChange = this.onIncidentDataChange.bind(this);
        this.onIncidentDelete = this.onIncidentDelete.bind(this);
        this.getIncident = this.getIncident.bind(this);
    }

    private getIncident(): IIncidentInfo {
        return {
            category: this.state.category,
            violation: this.state.violation,
            violationCode: this.state.violationCode,
            violationDate: this.state.violationDate,
            convictionDate: this.state.convictionDate
        };
    }

    onIncidentCategoryChange(category: string) {
        var categoryViolations = this.props.violations.violationGroups
            .filter(v => v.groupName === category)[0]
            .violations;

        this.setState({ category: category, violation: "", violationCode: "", violationDate: new Date(), convictionDate: null, categoryViolations: categoryViolations },
            () => this.props.onChange(this.getIncident(), this.props.index));
    }

    onIncidentViolationDateChange(value: Date) {
        let convictionDate = this.state.convictionDate;

        if (convictionDate && convictionDate < value) {
            convictionDate = value;
        }

        this.setState({ violationDate: value, convictionDate: convictionDate }, () => this.props.onChange(this.getIncident(), this.props.index));
    }

    onIncidentConvictionDateChange(value: Date) {
        this.setState({ convictionDate: new Date(value) }, () => this.props.onChange(this.getIncident(), this.props.index));
    }

    onIncidentDataChange(data) {
        this.setState({ violation: data.target.selectedOptions[0].innerText, violationCode: data.target.value }, () => this.props.onChange(this.getIncident(), this.props.index));
    }

    onIncidentDelete() {
        this.props.onDelete(this.props.index);
    }

    getIncidentYears() {
        let years = [];
        const currentYear = new Date().getFullYear();
        for (let i = currentYear; i >= currentYear - 5; i--) {
            years.push(i);
        }
        return years;
    }

	render() {
        return (
            <div className="incident-group" key={Utilities.uniqueId()}>
                <div className="incident-group-header"><label>INCIDENT #{this.props.index + 1}</label>
                    <a className="remove-incident-icon" onClick={this.onIncidentDelete}>Remove <i className="far fa-window-close"></i></a>
                </div>

                <ValidatableForm hasErrors={null != this.props.errors}>
                    <Form.Group>
                        <Form.Label>Select the incident category</Form.Label>
                        <Form.Control as="select"
                            id={"incidentCategory"}
                            value={this.state.category}
                            onChange={(e) => { this.onIncidentCategoryChange(e.target.value) }}>
                            <option value="">Select</option>
                            {this.props.violations.violationGroups.map((o) => <option key={Utilities.uniqueId()} value={o.groupName}>{o.groupName}</option>)}
                        </Form.Control>
                        {
                            null != this.props.errors && validationUtils.hasValidationErrorFor(this.props.errors, "incidents.category") && this.props.incident.category === ""
                            ? <Form.Control.Feedback type="invalid">
                                    {validationUtils.getValidationErrorFor(this.props.errors, "incidents.category")}
                            </Form.Control.Feedback>
                            : null
                        }
                    </Form.Group>

                    <Form.Group>
                        <Form.Label>Select the option that best describes the incident </Form.Label>
                        <Form.Control as="select"
                            value={this.state.violationCode}
                            disabled={!this.state.category}
                            onChange={(e) => { this.onIncidentDataChange(e) }}>
                            <option value="">Select</option>
                            {this.state.categoryViolations.map((o) => <option key={Utilities.uniqueId()} value={o.value}>{o.text}</option>)}
                        </Form.Control>
                        {
                            null != this.props.errors && validationUtils.hasValidationErrorFor(this.props.errors, "incidents.violationCode") && this.props.incident.violationCode === ""
                                ? <Form.Control.Feedback type="invalid">
                                    {validationUtils.getValidationErrorFor(this.props.errors, "incidents.violationCode")}
                                </Form.Control.Feedback>
                                : null
                        }
                    </Form.Group>

                    <Form.Group className="col-left">
                        <Form.Label>Violation Date</Form.Label>
                        <Calendar
                            id="violationDate"
                            value={moment(this.state.violationDate).toDate()}
                            minDate={moment(new Date()).add(-5, 'y').toDate()}
                            maxDate={moment(new Date()).toDate()}
                            onChange={this.onIncidentViolationDateChange}
                            validated={false}
                            validationError={""}
                        />
                    </Form.Group>

                    <Form.Group className="col-right">
                        <Form.Label>Conviction Date</Form.Label>
                        <DatePicker
                            key={"convictionDatedatePicker"}
                            className={"form-control"}
                            placeholder={"Conviction Date"}
                            maxLength={10}
                            minDate={moment(this.state.violationDate).toDate()}
                            maxDate={moment(new Date()).toDate()}
                            onChange={this.onIncidentConvictionDateChange}
                            selected={this.state.convictionDate ? moment(this.state.convictionDate).toDate() : null}
                            disabled={false} />
                    </Form.Group>
                </ValidatableForm>
            </div>
		);
	}
}

export default IncidentInfo;